import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useGetUnderwritingData } from '@/hooks/use-underwriting';
import { Loader2, Search, Download, TrendingUp, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { exportToCSV, SupplierUnderwritingData } from '@/services/reportService';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const UnderwritingDashboard = () => {
    const { data: underwritingData, isLoading, error } = useGetUnderwritingData();
    const [searchTerm, setSearchTerm] = useState('');

    const filteredData = underwritingData?.filter(item =>
        item.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.region?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.supplier_type?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatCurrency = (amount: number | null) => {
        if (amount === null) return 'ETB 0';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'ETB',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const formatPercent = (value: number | null) => {
        if (value === null) return '0%';
        return `${(value * 100).toFixed(1)}%`;
    };

    const getRiskBadge = (item: SupplierUnderwritingData) => {
        const fulfillment = item.fulfillment_rate || 0;
        const damage = item.damage_rate || 0;
        const payment = item.payment_performance || 0;

        if (fulfillment > 0.9 && damage < 0.05 && payment > 0.95) {
            return <Badge className="bg-green-100 text-green-800 border-green-200">Low Risk</Badge>;
        }
        if (fulfillment < 0.7 || damage > 0.15 || payment < 0.8) {
            return <Badge className="bg-red-100 text-red-800 border-red-200">High Risk</Badge>;
        }
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Medium Risk</Badge>;
    };

    if (isLoading) {
        return (
            <Layout>
                <div className="flex flex-col items-center justify-center h-[60vh]">
                    <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                    <p className="text-muted-foreground animate-pulse">Analyzing supplier history...</p>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="flex flex-col items-center justify-center h-[60vh] text-center px-4">
                    <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
                    <h2 className="text-xl font-semibold mb-2">Failed to load underwriting data</h2>
                    <p className="text-muted-foreground">There was an error connecting to the underwriting engine. Please try again later.</p>
                </div>
            </Layout>
        );
    }

    // Summary Metrics
    const totalValue = underwritingData?.reduce((sum, item) => sum + (item.total_value_birr || 0), 0) || 0;
    const avgFulfillment = (underwritingData?.reduce((sum, item) => sum + (item.fulfillment_rate || 0), 0) || 0) / (underwritingData?.length || 1);
    const totalHighRisk = underwritingData?.filter(item => (item.fulfillment_rate || 0) < 0.7 || (item.damage_rate || 0) > 0.15).length || 0;

    return (
        <Layout>
            <div className="space-y-6 animate-in fade-in duration-500">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Supplier Credit Underwriting</h1>
                        <p className="text-muted-foreground">Comprehensive history and trust metrics for credit assessment</p>
                    </div>
                    <Button onClick={() => underwritingData && exportToCSV(underwritingData, 'supplier_underwriting')} variant="outline">
                        <Download className="mr-2 h-4 w-4" />
                        Export Dataset
                    </Button>
                </div>

                {/* Metric Cards */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <Card className="bg-gradient-to-br from-white to-slate-50 border-none shadow-sm overflow-hidden relative">
                        <div className="absolute top-0 right-0 p-4 opacity-10">
                            <TrendingUp className="h-12 w-12 text-primary" />
                        </div>
                        <CardHeader className="pb-2">
                            <CardDescription className="text-xs font-semibold uppercase tracking-wider">Total Portfolio Value</CardDescription>
                            <CardTitle className="text-2xl">{formatCurrency(totalValue)}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                                <CheckCircle className="h-3 w-3 text-green-500" />
                                Aggregated across {underwritingData?.length} suppliers
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-white to-slate-50 border-none shadow-sm overflow-hidden relative">
                        <div className="absolute top-0 right-0 p-4 opacity-10">
                            <CheckCircle className="h-12 w-12 text-green-500" />
                        </div>
                        <CardHeader className="pb-2">
                            <CardDescription className="text-xs font-semibold uppercase tracking-wider">Avg Fulfillment Rate</CardDescription>
                            <CardTitle className="text-2xl">{formatPercent(avgFulfillment)}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                                <div
                                    className="bg-green-500 h-full transition-all duration-1000"
                                    style={{ width: `${avgFulfillment * 100}%` }}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-white to-red-50 border-none shadow-sm overflow-hidden relative border-l-4 border-l-red-400">
                        <div className="absolute top-0 right-0 p-4 opacity-10">
                            <AlertTriangle className="h-12 w-12 text-red-500" />
                        </div>
                        <CardHeader className="pb-2">
                            <CardDescription className="text-xs font-semibold uppercase tracking-wider">High Risk Entities</CardDescription>
                            <CardTitle className="text-2xl text-red-600">{totalHighRisk}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-xs text-muted-foreground">Entities below 70% fulfillment threshold</p>
                        </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-white to-slate-50 border-none shadow-sm overflow-hidden relative">
                        <div className="absolute top-0 right-0 p-4 opacity-10">
                            <Info className="h-12 w-12 text-slate-400" />
                        </div>
                        <CardHeader className="pb-2">
                            <CardDescription className="text-xs font-semibold uppercase tracking-wider">Avg Payment Terms</CardDescription>
                            <CardTitle className="text-2xl">
                                {Math.round((underwritingData?.reduce((sum, item) => sum + (item.avg_payment_term_days || 0), 0) || 0) / (underwritingData?.length || 1))} Days
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-xs text-muted-foreground">Average credit period granted</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Data Table */}
                <Card className="border-none shadow-md overflow-hidden">
                    <CardHeader className="bg-slate-50/50 border-b pb-4">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div>
                                <CardTitle className="text-lg">Supplier Performance Matrix</CardTitle>
                                <CardDescription>Consolidated metrics from Procurement, Warehouse, and Finance</CardDescription>
                            </div>
                            <div className="relative w-full md:w-72">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Filter suppliers or regions..."
                                    className="pl-9 h-9"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader className="bg-slate-50/50">
                                    <TableRow>
                                        <TableHead className="font-semibold px-4 py-3">Supplier</TableHead>
                                        <TableHead className="font-semibold">Type</TableHead>
                                        <TableHead className="font-semibold text-right">Orders</TableHead>
                                        <TableHead className="font-semibold text-right">Value (ETB)</TableHead>
                                        <TableHead className="font-semibold text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                Fulfillment
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger><Info className="h-3 w-3 text-muted-foreground" /></TooltipTrigger>
                                                        <TooltipContent>Received vs Ordered quantity ratio</TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </div>
                                        </TableHead>
                                        <TableHead className="font-semibold text-center">Damage %</TableHead>
                                        <TableHead className="font-semibold text-center">Payment Perf.</TableHead>
                                        <TableHead className="font-semibold text-center">Consistency</TableHead>
                                        <TableHead className="font-semibold text-right">Underwriting</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredData?.map((item) => (
                                        <TableRow key={item.entity_id} className="hover:bg-slate-50/80 transition-colors">
                                            <TableCell className="px-4 py-4">
                                                <div className="flex flex-col">
                                                    <span className="font-semibold text-slate-900">{item.supplier_name}</span>
                                                    <span className="text-xs text-muted-foreground">{item.region || 'No region'}</span>
                                                    {item.coordinator_name && (
                                                        <span className="text-[10px] mt-1 text-primary/70 font-medium italic">Coord: {item.coordinator_name}</span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="capitalize text-[10px] py-0">
                                                    {item.supplier_type}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right font-medium">{item.total_orders}</TableCell>
                                            <TableCell className="text-right tabular-nums">{formatCurrency(item.total_value_birr)}</TableCell>
                                            <TableCell className="text-center">
                                                <span className={cn(
                                                    "px-2 py-0.5 rounded text-xs font-bold",
                                                    (item.fulfillment_rate || 0) > 0.9 ? "bg-green-50 text-green-700" :
                                                        (item.fulfillment_rate || 0) < 0.7 ? "bg-red-50 text-red-700" : "bg-yellow-50 text-yellow-700"
                                                )}>
                                                    {formatPercent(item.fulfillment_rate)}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-center tabular-nums">
                                                <span className={cn(
                                                    "text-xs",
                                                    (item.damage_rate || 0) > 0.1 ? "text-red-600 font-bold" : "text-slate-600"
                                                )}>
                                                    {formatPercent(item.damage_rate)}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <span className={cn(
                                                    "text-xs underline decoration-dotted",
                                                    (item.payment_performance || 0) > 0.95 ? "text-green-600" : "text-slate-600"
                                                )}>
                                                    {formatPercent(item.payment_performance)}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <Badge variant="secondary" className="font-mono text-[10px]">
                                                    σ: {item.consistency_variability_score?.toFixed(1) || '0.0'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {getRiskBadge(item)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {filteredData?.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={9} className="h-32 text-center text-muted-foreground">
                                                No suppliers match your search criteria.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
};

export default UnderwritingDashboard;
